/**
 * Centralized configuration for the V4 frontend.
 * 
 * All API URL constants derive from NEXT_PUBLIC_API_URL.
 * In development/staging, this env var MUST be set — the production
 * fallback only exists as a safety net, not as default config.
 */

if (typeof window !== 'undefined' && !process.env.NEXT_PUBLIC_API_URL) {
  console.warn(
    '[VHire Config] NEXT_PUBLIC_API_URL is not set. Falling back to production API. ' +
    'Set NEXT_PUBLIC_API_URL in your .env.local to avoid sending requests to production.'
  );
}

/**
 * API base URL WITH /api suffix.
 * Use for endpoints like: `${API_BASE}/jobs`, `${API_BASE}/candidates/me`
 */
export const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://api.vhire.com/api';

/**
 * API origin WITHOUT /api suffix.
 * Use for endpoints that include their own path prefix,
 * e.g. `${API_ORIGIN}/api/vault/session/...`
 */
export const API_ORIGIN = API_BASE.replace(/\/api$/, '');

/**
 * WebSocket base URL. Derived from API_BASE so it stays in sync.
 * Override with NEXT_PUBLIC_WS_URL for non-default deployments.
 */
export const WS_BASE =
  process.env.NEXT_PUBLIC_WS_URL ||
  API_ORIGIN.replace(/^http/, 'ws');

/**
 * Backwards-compatible accessor. Prefer importing API_BASE directly.
 */
export const getApiBase = () => API_BASE;

/**
 * Tenant-aware URL primitives, reserved-subdomain enforcement, and CORS
 * helpers — single source of truth across the monorepo.
 */
export {
  ROOT_DOMAIN,
  APP_BASE,
  ASSETS_BASE,
  tenantUrl,
  buildAuthLink,
  RESERVED_SUBDOMAINS,
  isReservedSubdomain,
  isAllowedOrigin,
  vhireOriginRegex,
  corsDecision,
} from '@vhire/urls';

export type {
  TenantUrlContext,
  CorsDecision,
  CorsReason,
} from '@vhire/urls';
